import type { ObjectReferenceSummary } from '@heidi/agents';
import {
  hasPatientNameFields,
  objectDayLabel,
  resolvePatientAvatarSeed,
  resolvePatientLabel,
  sessionDurationLabel,
  sessionTitleNamesPatient,
  sessionTokenIdentity,
  sessionTokenLabel,
  type PatientNameFields,
  type SessionTokenLabelSource,
} from '@heidi/agents/data';
import type { CollectionResponse } from '@heidi/evidence/contracts';
import { calculateAge, type IdentityEditorIntl } from '@heidi/patients';
import type { PatientProfileWithSessionCount } from '@heidi/patients/contracts';
import {
  DocumentGenType,
  Shared__enums__enums__Gender as Gender,
  TemplateVisibility,
  type components,
} from '@heidi/schema/ml-scribe';
import type {
  QueryDocumentResponse,
  QuerySessionResponse,
} from '@heidi/session/contracts';
import { getDisplayableSessionGist } from '@heidi/session/contracts';
import type { ClinicalTask } from '@heidi/tasks/contracts';
import type { TemplateResponse } from '@heidi/templates/contracts';
import { parseTimeStamp, parseTimeStampOrNull } from '@heidi/utils';
import { isValid, parseISO, startOfDay } from 'date-fns';
import type { IntlShape } from 'react-intl';

type PatientNameSource = PatientNameFields | null | undefined;
type Named = { name?: string | null } | null | undefined;
type Titled = { title?: string | null } | null | undefined;

const named = (value: Named): string | undefined => value?.name?.trim() || undefined;

export const referenceLabels = {
  patient: (profile: PatientNameSource): string | undefined =>
    resolvePatientLabel(profile),
  session: (
    session: SessionTokenLabelSource | null | undefined,
    intl: IntlShape
  ): string | undefined =>
    session
      ? sessionTokenLabel(sessionTokenIdentity(session, intl, parseTimeStamp))
      : undefined,
  document: named,
  template: named,
  routine: named,
  project: (project: Titled): string | undefined => project?.title?.trim() || undefined,
  task: (
    task: Pick<ClinicalTask, 'title' | 'short_title'> | null | undefined
  ): string | undefined => task?.title?.trim() || task?.short_title?.trim() || undefined,
  // Never `subject_line`: it carries the patient's name.
  letter: named,
  file: (file: { filename?: string | null } | null | undefined): string | undefined =>
    file?.filename?.trim() || undefined,
  collection: named,
  source: (labelHint: string | undefined): string | undefined =>
    labelHint?.trim() || undefined,
};

const sessionProfile = (session: SessionTokenLabelSource) =>
  hasPatientNameFields(session.patient_profile) ? session.patient_profile : undefined;

export const sessionPatientName = (
  session: SessionTokenLabelSource | null | undefined
): string | undefined =>
  session
    ? resolvePatientLabel(sessionProfile(session), session.patient?.name)
    : undefined;

export const sessionAvatarSeed = (
  session: SessionTokenLabelSource | null | undefined
): string | undefined =>
  session && sessionPatientName(session)
    ? resolvePatientAvatarSeed(sessionProfile(session), session.patient?.name)
    : undefined;

type PatientSummarySource = Pick<
  PatientProfileWithSessionCount,
  | 'gender'
  | 'birth_date'
  | 'last_session_created_at'
  | 'demographic_string'
  | 'first_name'
  | 'last_name'
> & {
  // Typed required, but the by-id endpoint omits it until its enrichment ships.
  readonly session_count?: number | null;
};

type SessionSummarySource = SessionTokenLabelSource &
  Partial<Pick<QuerySessionResponse, 'session_gist' | 'duration'>>;

type DocumentSummarySource = Pick<QueryDocumentResponse, 'generation_type'>;

type TemplateSummarySource = Pick<
  TemplateResponse,
  'description' | 'template_visibility' | 'updated_time' | 'usage'
>;

type TaskSummarySource = Pick<
  ClinicalTask,
  'description' | 'status' | 'due_date' | 'patient_name'
>;

type LetterSummarySource = Pick<components['schemas']['LetterResponse'], 'updated_at'>;

type CollectionSummarySource = Pick<
  CollectionResponse,
  'description' | 'document_count' | 'updated_at'
>;

const OPEN_TASK_STATUSES: ReadonlySet<string> = new Set(['todo', 'in_progress']);

// `due_date` is a calendar day; `parseISO` reads it as local midnight, not UTC.
const taskDueField = (
  task: TaskSummarySource,
  intl: IntlShape,
  now: Date
): ObjectReferenceSummary['metadata'][number] => {
  const due = task.due_date ? parseISO(task.due_date.slice(0, 10)) : null;
  if (!due || !isValid(due)) return { kind: 'dueDate' };
  return {
    kind: 'dueDate',
    value: intl.formatDate(due, { day: 'numeric', month: 'short' }),
    overdue: OPEN_TASK_STATUSES.has(task.status) && due < startOfDay(now),
  };
};

export const sessionStartedAt = (session: SessionTokenLabelSource): Date | null =>
  parseTimeStampOrNull(
    session.created_time_local ?? session.created_at,
    !session.ehr_appt_id
  );

export const sessionGist = (
  session: SessionSummarySource,
  intl: IntlShape
): string | undefined =>
  sessionTokenIdentity(session, intl, parseTimeStamp).title ??
  getDisplayableSessionGist(session.session_gist);

export type PatientIdentityFormat = Pick<
  IdentityEditorIntl,
  'ageFormat' | 'formatGender'
>;

export const patientSexAge = (
  profile: Pick<PatientSummarySource, 'gender' | 'birth_date'>,
  intl: IntlShape,
  identity: PatientIdentityFormat
): string | null => {
  const age = calculateAge(profile.birth_date);
  const parts = [
    profile.gender && profile.gender !== Gender.unknown
      ? identity.formatGender(profile.gender)
      : null,
    age === null ? null : identity.ageFormat(age),
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0
    ? intl.formatList(parts, { type: 'unit', style: 'short' })
    : null;
};

const HEIDI_GENERATED_DOCUMENTS: ReadonlySet<string> = new Set([
  DocumentGenType.TEMPLATE,
  DocumentGenType.INTERNAL_TEMPLATE,
  DocumentGenType.ASK_AI,
  DocumentGenType.PRESET_AI_COMMAND,
]);

const TEMPLATE_OWNER: Partial<Record<string, 'personal' | 'team' | 'community'>> = {
  [TemplateVisibility.User]: 'personal',
  [TemplateVisibility.Team]: 'team',
  [TemplateVisibility.Community]: 'community',
};

const withText = (value: string | null | undefined): string | undefined =>
  value?.trim() || undefined;

export const referenceSummaries = {
  patient: (
    title: string,
    profile: PatientSummarySource,
    intl: IntlShape,
    identity: PatientIdentityFormat
  ): ObjectReferenceSummary => ({
    kind: 'patient',
    title,
    avatarSeed: resolvePatientAvatarSeed(profile),
    metadata: [
      { kind: 'sexAge', value: patientSexAge(profile, intl, identity) },
      {
        kind: 'lastSeen',
        value: objectDayLabel(profile.last_session_created_at, intl, parseTimeStamp),
      },
      { kind: 'sessionCount', count: profile.session_count },
    ],
  }),
  session: (session: SessionSummarySource, intl: IntlShape): ObjectReferenceSummary => {
    const identity = sessionTokenIdentity(session, intl, parseTimeStamp);
    const { patientName } = identity;
    const avatarSeed = sessionAvatarSeed(session);
    return {
      kind: 'session',
      title: identity.title ?? identity.dayTime,
      ...(identity.title ? { titleDetail: identity.dayTime } : {}),
      ...(avatarSeed ? { avatarSeed } : {}),
      summary: getDisplayableSessionGist(session.session_gist),
      metadata: [
        {
          kind: 'patientLink',
          status: !patientName
            ? 'unlinked'
            : sessionTitleNamesPatient(identity.title, patientName)
              ? null
              : 'linked',
          patientName,
        },
        { kind: 'duration', value: sessionDurationLabel(session.duration) },
      ],
    };
  },
  document: (title: string, document: DocumentSummarySource): ObjectReferenceSummary => ({
    kind: 'document',
    title,
    metadata: [
      {
        kind: 'source',
        source:
          document.generation_type &&
          HEIDI_GENERATED_DOCUMENTS.has(document.generation_type)
            ? 'generatedByHeidi'
            : null,
      },
    ],
  }),
  template: (
    title: string,
    template: TemplateSummarySource,
    intl: IntlShape
  ): ObjectReferenceSummary => ({
    kind: 'template',
    title,
    summary: withText(template.description),
    metadata: [
      { kind: 'owner', owner: TEMPLATE_OWNER[template.template_visibility] ?? null },
      {
        kind: 'lastEdited',
        value: objectDayLabel(template.updated_time, intl, parseTimeStamp),
      },
      { kind: 'timesUsed', count: template.usage },
    ],
  }),
  task: (
    title: string,
    task: TaskSummarySource,
    intl: IntlShape,
    now: Date = new Date()
  ): ObjectReferenceSummary => ({
    kind: 'task',
    title,
    summary: withText(task.description),
    metadata: [
      {
        kind: 'patientLink',
        status: withText(task.patient_name) ? 'linked' : null,
        patientName: task.patient_name,
      },
      { kind: 'taskStatus', status: task.status },
      taskDueField(task, intl, now),
    ],
  }),
  letter: (
    title: string,
    letter: LetterSummarySource,
    intl: IntlShape
  ): ObjectReferenceSummary => ({
    kind: 'letter',
    title,
    metadata: [
      {
        kind: 'lastEdited',
        value: objectDayLabel(letter.updated_at, intl, parseTimeStamp),
      },
    ],
  }),
  collection: (
    title: string,
    collection: CollectionSummarySource,
    intl: IntlShape
  ): ObjectReferenceSummary => ({
    kind: 'collection',
    title,
    summary: withText(collection.description),
    metadata: [
      { kind: 'documentCount', count: collection.document_count },
      {
        kind: 'lastEdited',
        value: objectDayLabel(collection.updated_at, intl, parseTimeStamp),
      },
    ],
  }),
};
